/**
 * Trainingsplaene (F-34 A) - rein und getestet: welcher Plan und welche
 * Einheiten an einem Tag gelten (Versionen, A/B-Woche, Zyklus) und wie lange
 * eine Einheit voraussichtlich dauert.
 */
import { addDays, differenceInDays, startOfWeek, weekday } from '@/lib/datetime';
import type { LocalDate } from '@/domain/types';
import type { PlanEinheit, PlanSatz, PlanUebung, TrainingsPlan, Uebung, Wochentag } from '@/domain/training/typen';

/** Montag = 1 … Sonntag = 7 (ISO). */
export const isoWochentag = (d: LocalDate): Wochentag => (((weekday(d) + 6) % 7) + 1) as Wochentag;

/** Welche Woche im Wechsel bzw. Zyklus (1..n) ein Datum ist. */
export function zyklusWoche(plan: Pick<TrainingsPlan, 'wochen' | 'zyklusStart' | 'gueltigAb'>, datum: LocalDate): number {
  if (plan.wochen <= 1) return 1;
  const start = startOfWeek(plan.zyklusStart ?? plan.gueltigAb);
  const wochen = Math.round(differenceInDays(start, startOfWeek(datum)) / 7);
  return (((wochen % plan.wochen) + plan.wochen) % plan.wochen) + 1;
}

/** Die Version eines Plans, die an einem Tag gilt. */
export function versionAm(plaene: TrainingsPlan[], familie: string, datum: LocalDate): TrainingsPlan | null {
  return plaene
    .filter((p) => p.familie === familie && p.gueltigAb <= datum && (!p.gueltigBis || datum < p.gueltigBis))
    .sort((a, b) => b.version - a.version)[0] ?? null;
}

/** Der aktive Plan (seine Familie) in der Version, die am Tag gilt. */
export function planAm(plaene: TrainingsPlan[], datum: LocalDate): TrainingsPlan | null {
  const aktiv = plaene.find((p) => p.aktiv && !p.gueltigBis) ?? plaene.find((p) => p.aktiv);
  return aktiv ? versionAm(plaene, aktiv.familie, datum) : null;
}

export function einheitenAm(plan: TrainingsPlan, einheiten: PlanEinheit[], datum: LocalDate): PlanEinheit[] {
  const tag = isoWochentag(datum);
  const woche = zyklusWoche(plan, datum);
  return einheiten
    .filter((e) => e.planId === plan.id && e.wochentag === tag && (e.woche === undefined || e.woche === woche))
    .sort((a, b) => (a.uhrzeit ?? '99').localeCompare(b.uhrzeit ?? '99') || a.order - b.order);
}

// --- Dauer -------------------------------------------------------------------

/** Annahmen der Schaetzung - sichtbar in der App, ueberschreibbar je Einheit. */
export const DAUER = {
  /** Sekunden je Wiederholung (kontrolliertes Tempo) plus Anlauf je Satz. */
  sekJeWdh: 3,
  sekJeSatz: 10,
  /** Umbauen, Gewichte holen je Uebung. */
  wechselSek: 60,
  pauseSek: 90,
  aufwaermenMin: 10,
  zirkelArbeitSek: 60,
};

function satzSek(s: PlanSatz): number {
  if (s.dauerSek) return s.dauerSek;
  const wdh = s.wdhBis && s.wdh ? (s.wdh + s.wdhBis) / 2 : s.wdh ?? s.wdhBis ?? 10;
  return wdh * DAUER.sekJeWdh + DAUER.sekJeSatz;
}

const pauseVon = (pu: PlanUebung, u: Uebung | undefined) => pu.pauseSek ?? u?.standard.pauseSek ?? DAUER.pauseSek;

/** Aufeinanderfolgende Uebungen mit gleichem `block` bilden einen Supersatz/Zirkel. */
export function bloecke(uebungen: PlanUebung[]): PlanUebung[][] {
  const out: PlanUebung[][] = [];
  for (const u of uebungen) {
    const letzter = out[out.length - 1];
    if (letzter && u.block && letzter[0]!.block === u.block) letzter.push(u);
    else out.push([u]);
  }
  return out;
}

export interface DauerSchaetzung {
  minuten: number;
  aufwaermenMin: number;
  arbeitMin: number;
  pausenMin: number;
  /** `true`, wenn die Dauer von Hand gesetzt ist. */
  manuell: boolean;
}

const aufrunden = (min: number) => Math.ceil(min / 5) * 5;

export function dauerSchaetzen(e: PlanEinheit, bibliothek: ReadonlyMap<string, Uebung>, plan?: Pick<TrainingsPlan, 'aufwaermen'>): DauerSchaetzung {
  const hatAufwaermen = Boolean(e.aufwaermen?.trim() || plan?.aufwaermen?.[e.typ]?.trim());
  const aufwaermenMin = e.aufwaermenMin ?? (hatAufwaermen ? DAUER.aufwaermenMin : 0);
  let arbeit = 0;
  let pausen = 0;

  if (e.typ === 'ausdauer') {
    const a = e.ausdauer ?? {};
    arbeit = a.dauerMin ? a.dauerMin * 60 : a.distanzKm && a.paceSek ? a.distanzKm * a.paceSek : 0;
  } else if (e.typ === 'zirkel') {
    const z = e.zirkel ?? { runden: 1 };
    const stationen = e.uebungen.map((pu) => pu.saetze[0]?.dauerSek ?? z.arbeitSek ?? DAUER.zirkelArbeitSek);
    arbeit = z.runden * stationen.reduce((s, x) => s + x, 0);
    pausen = z.runden * Math.max(0, stationen.length - 1) * (z.pauseSek ?? 0) + Math.max(0, z.runden - 1) * (z.rundenPauseSek ?? 0);
  } else {
    const alle = bloecke(e.uebungen);
    alle.forEach((block, bi) => {
      const runden = Math.max(...block.map((pu) => pu.saetze.length));
      arbeit += block.length * DAUER.wechselSek;
      for (let r = 0; r < runden; r++) {
        for (const pu of block) { const s = pu.saetze[r]; if (s) arbeit += satzSek(s); }
        const letzteRunde = r === runden - 1 && bi === alle.length - 1;
        if (!letzteRunde) pausen += pauseVon(block[block.length - 1]!, bibliothek.get(block[block.length - 1]!.uebungId));
      }
    });
  }

  const geschaetzt = aufrunden(aufwaermenMin + (arbeit + pausen) / 60);
  return {
    minuten: e.dauerMin ?? geschaetzt,
    aufwaermenMin,
    arbeitMin: Math.round(arbeit / 60),
    pausenMin: Math.round(pausen / 60),
    manuell: e.dauerMin !== undefined,
  };
}

/** Saetze aus den Standardwerten einer Uebung - beim Einplanen. */
export function standardSaetze(u: Pick<Uebung, 'standard'>): PlanSatz[] {
  const s = u.standard;
  const satz: PlanSatz = {};
  if (s.wdh) satz.wdh = s.wdh;
  if (s.wdhBis) satz.wdhBis = s.wdhBis;
  if (s.gewicht) satz.gewicht = s.gewicht;
  if (s.dauerSek) satz.dauerSek = s.dauerSek;
  return Array.from({ length: Math.max(1, s.saetze ?? 3) }, () => ({ ...satz }));
}

/** Montag der Woche eines Datums - Anker fuer den Zyklus. */
export const wochenMontag = (d: LocalDate) => startOfWeek(d);

/** Erster Tag nach einem Datum - fuer „gueltig bis“ der Vorversion. */
export const tagDanach = (d: LocalDate) => addDays(d, 1);
